import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    getFormattedStartMonth,
    getFormattedEndMonth,
    getDisabledDates,
    getFormattedSoldOutDates,
    getFormattedEventDates,
    getCalendarColors,
    seriesHasSchedule,
    getCurrentMonth,
    needsDatesLoaded,
} from '../../series/selectors/eventSeries';

import { 
    setEventTimes,
    setSelectedDate,
    setSelectedChildEvent,
    handleMonthChange,
    lazyLoadSeriesDates,
} from '../../series/actions/event-series';
import DayPicker from '../../series/components/DayPicker';
import { isValidDateObject } from '../../../../helpers/time';
import { getSubState } from '../../../reducers/subReducerHelpers';

class UpsellDayPicker extends Component {

    state = {
        calendarLoading: false,
    }

    componentDidMount = async () => {
        if (needsDatesLoaded(this.props)) {
            this.setState({calendarLoading: true});
            try {
                this.props.lazyLoadSeriesDates(this.props.currentMonth, 1, this.props.reducerKey);
            } catch (err) {
                console.error(err);
            }
            this.setState({calendarLoading: false});
        }        
    }

    onDayClick =  async (day, modifiers = {}) => {
        if (!!this.state.calendarLoading || !!modifiers.disabled) {
            return false;
        }

        this.clearEventTimes();

        //if changing dates after selecting a child event, remove the selected child event
        let selectedDate = new Date(this.props.selectedEventDate);
        if (
            isValidDateObject(selectedDate) &&
            selectedDate.toLocaleDateString("en-US") !== day.toLocaleDateString("en-US")
        ) {
            this.clearSelectedChildEvent();
        }

        const newDateString = await this.props.setSelectedDate(day, this.props.reducerKey);
        this.props.onDayPick(newDateString);
    }
        
    onMonthChange = async (inputMonth) => {
        const month = new Date(inputMonth.getTime() - inputMonth.getTimezoneOffset() * 60000);
        this.clearEventTimes();
        this.clearSelectedChildEvent();
        this.props.setSelectedDate(null, this.props.reducerKey);
        this.setState({calendarLoading: true});
        try {
            await this.props.handleMonthChange(month, 1, this.props.reducerKey);
        } catch (err) {
            console.error(err);
        }
        this.setState({calendarLoading: false});
    }

    clearEventTimes = () => {
        this.props.setEventTimes([], null, this.props.reducerKey);
    }

    clearSelectedChildEvent = () => {
        this.props.setSelectedChildEvent(null, this.props.reducerKey);
    }

    render() {
        if (!this.props.hasSchedule) {
            return null;
        }

        const providedProps = {...this.props};
        providedProps.loading = this.state.calendarLoading;
        providedProps.eventTimesLoading = this.state.calendarLoading;

        return (
            <DayPicker 
                onDayClick={this.onDayClick}
                onMonthChange={this.onMonthChange}
                {...providedProps} 
            />
        );
    }
};

const mapStateToProps = (state, ownProps) => {

    // put some things where the event series selectors expect them to be
    const modifiedState = {...state};
    modifiedState.event       = getSubState(state.event, ownProps.reducerKey);
    modifiedState.ui          = getSubState(state.ui, ownProps.reducerKey);
    modifiedState.eventSeries = getSubState(state.eventSeries, ownProps.reducerKey);

    //all kinds of things crash here if the series does not have a schedule
    let hasSchedule = seriesHasSchedule(modifiedState);
    if (!hasSchedule) {
        return {
            locale: state.intl.locale,
            selectedDays: [],
            hasSchedule,
        };
    }

    return {
        locale: state.intl.locale,
        numberOfMonths: 1,
        startMonth: getFormattedStartMonth(modifiedState),
        defaultMonth: getFormattedStartMonth(modifiedState),
        endMonth: getFormattedEndMonth(modifiedState),
        disabled: getDisabledDates(modifiedState),
        soldOutDays: getFormattedSoldOutDates(modifiedState),
        eventDates: getFormattedEventDates(modifiedState),
        calendarColors: getCalendarColors(modifiedState),
        selectedDays: getFormattedEventDates(modifiedState),
        accentColor: state.ui.styleVars.GLOBAL__COLOR__ACCENT,
        month: getCurrentMonth(modifiedState),
        selectedEventDate: modifiedState.eventSeries.selectedDate,
        loadedDates: state.eventSeries.datesLazyLoaded, // necessary for needsDatesLoaded
        hasSchedule,
    };
};

const mapDispatchToProps = dispatch => {
    return bindActionCreators({
        setEventTimes,
        setSelectedChildEvent,
        setSelectedDate,
        handleMonthChange,
        lazyLoadSeriesDates,
    }, dispatch);
};

UpsellDayPicker.propTypes = {
    event: PropTypes.object.isRequired,
    reducerKey: PropTypes.string.isRequired,
    onDayPick: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(UpsellDayPicker);
